import { apiClient } from './api';
import {
  PaleontologyStartRequest,
  PaleontologyStartResponse,
  PaleontologyIterationRequest,
  PaleontologyIterationResponse,
  PaleontologySession,
  PaleontologyAgentType,
  PaleontologyVisualization,
} from '../models/paleontologyAgent';

const SESSION_STORAGE_KEY = 'paleontologyAgentSessions';

export class PaleontologyAgentService {
  // API calls
  static async startSession(agentType: PaleontologyAgentType, request: PaleontologyStartRequest): Promise<PaleontologyStartResponse> {
    const response = await apiClient.post(`/api/${agentType}/session/start`, request);
    return response.data;
  }

  static async iterate(
    agentType: PaleontologyAgentType, 
    sessionId: string, 
    request: PaleontologyIterationRequest
  ): Promise<PaleontologyIterationResponse> {
    const response = await apiClient.post(`/api/${agentType}/session/${sessionId}/iterate`, request);
    return response.data;
  }

  // Local session management
  static createLocalSession(
    agentType: PaleontologyAgentType,
    apiSessionId: string,
    initialPrompt: string,
    visualization?: PaleontologyVisualization
  ): PaleontologySession {
    const session: PaleontologySession = {
      id: `paleo-${agentType}-${Date.now()}`,
      sessionId: apiSessionId,
      agentType,
      initialPrompt,
      visualizations: visualization ? [visualization] : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(id: string): PaleontologySession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === id) || null;
  }

  static getAllSessions(): PaleontologySession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static getSessionsByAgent(agentType: PaleontologyAgentType): PaleontologySession[] {
    const sessions = this.getAllSessions();
    return sessions.filter(s => s.agentType === agentType);
  }

  static saveSession(session: PaleontologySession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(id: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== id);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addVisualization(sessionId: string, visualization: PaleontologyVisualization): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.visualizations.push(visualization);
      this.saveSession(session);
    }
  }

  static exportSession(session: PaleontologySession): string {
    let content = `# Paleontology Analysis: ${session.agentType.charAt(0).toUpperCase() + session.agentType.slice(1)}\n\n`;
    content += `**Initial Prompt:** ${session.initialPrompt}\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n\n`;

    session.visualizations.forEach((viz, index) => {
      content += `## Analysis ${index + 1}: ${viz.title}\n\n`;
      content += `**Era:** ${viz.era}\n`;
      content += `**Period:** ${viz.period}\n`;
      if (viz.epoch) content += `**Epoch:** ${viz.epoch}\n`;
      content += `**Time Range:** ${viz.timeRange}\n`;
      content += `**Description:** ${viz.description}\n\n`;

      // Key Features
      if (viz.keyFeatures.length > 0) {
        content += `### Key Features\n`;
        viz.keyFeatures.forEach(feature => content += `- ${feature}\n`);
        content += '\n';
      }

      // Content sections
      const c = viz.content;
      content += `### Overview\n${c.overview}\n\n`;

      // Geological Context
      content += `### Geological Context\n`;
      content += `**Tectonic Activity:** ${c.geologicalContext.tectonicActivity}\n`;
      content += `**Continental Configuration:** ${c.geologicalContext.continentalConfiguration}\n`;
      if (c.geologicalContext.rockFormations.length > 0) {
        content += `**Rock Formations:**\n`;
        c.geologicalContext.rockFormations.forEach(rock => content += `- ${rock}\n`);
      }
      if (c.geologicalContext.mineralDeposits && c.geologicalContext.mineralDeposits.length > 0) {
        content += `**Mineral Deposits:**\n`;
        c.geologicalContext.mineralDeposits.forEach(mineral => content += `- ${mineral}\n`);
      }
      content += '\n';

      // Lifeforms
      content += `### Lifeforms\n`;
      
      if (c.lifeforms.dominantSpecies.length > 0) {
        content += `#### Dominant Species\n`;
        c.lifeforms.dominantSpecies.forEach(species => {
          content += `**${species.name}** (${species.type})\n`;
          species.characteristics.forEach(char => content += `  - ${char}\n`);
          if (species.significance) content += `  - Significance: ${species.significance}\n`;
        });
      }

      if (c.lifeforms.emergingSpecies.length > 0) {
        content += `\n#### Emerging Species\n`;
        c.lifeforms.emergingSpecies.forEach(species => {
          content += `**${species.name}** (${species.type})\n`;
          species.characteristics.forEach(char => content += `  - ${char}\n`);
          if (species.significance) content += `  - Significance: ${species.significance}\n`;
        });
      }

      if (c.lifeforms.extinctSpecies && c.lifeforms.extinctSpecies.length > 0) {
        content += `\n#### Extinct Species\n`;
        c.lifeforms.extinctSpecies.forEach(species => {
          content += `**${species.name}** (${species.type})\n`;
          species.characteristics.forEach(char => content += `  - ${char}\n`);
          if (species.significance) content += `  - Significance: ${species.significance}\n`;
        });
      }

      if (c.lifeforms.evolutionaryInnovations.length > 0) {
        content += `\n#### Evolutionary Innovations\n`;
        c.lifeforms.evolutionaryInnovations.forEach(innovation => content += `- ${innovation}\n`);
      }
      content += '\n';

      // Environment
      content += `### Environmental Conditions\n`;
      content += `**Climate:** ${c.environment.climate}\n`;
      content += `**Atmosphere:** ${c.environment.atmosphere}\n`;
      if (c.environment.oceanConditions) content += `**Ocean Conditions:** ${c.environment.oceanConditions}\n`;
      if (c.environment.vegetationType) content += `**Vegetation Type:** ${c.environment.vegetationType}\n`;
      if (c.environment.ecosystems.length > 0) {
        content += `**Ecosystems:**\n`;
        c.environment.ecosystems.forEach(eco => content += `- ${eco}\n`);
      }
      content += '\n';

      // Fossil Record
      content += `### Fossil Record\n`;
      if (c.fossilRecord.notableFossils.length > 0) {
        content += `#### Notable Fossils\n`;
        c.fossilRecord.notableFossils.forEach(fossil => {
          content += `**${fossil.name}** (${fossil.type})\n`;
          if (fossil.location) content += `  - Location: ${fossil.location}\n`;
          content += `  - Significance: ${fossil.significance}\n`;
        });
      }
      if (c.fossilRecord.preservationTypes.length > 0) {
        content += `\n**Preservation Types:**\n`;
        c.fossilRecord.preservationTypes.forEach(type => content += `- ${type}\n`);
      }
      if (c.fossilRecord.fossilSites && c.fossilRecord.fossilSites.length > 0) {
        content += `\n**Important Fossil Sites:**\n`;
        c.fossilRecord.fossilSites.forEach(site => content += `- ${site}\n`);
      }
      content += '\n';

      // Major Events
      if (c.majorEvents.length > 0) {
        content += `### Major Events\n`;
        c.majorEvents.forEach(event => content += `- ${event}\n`);
        content += '\n';
      }

      // Significance
      if (c.significance.length > 0) {
        content += `### Significance\n`;
        c.significance.forEach(sig => content += `- ${sig}\n`);
        content += '\n';
      }

      // Modern Connections
      if (c.modernConnections && c.modernConnections.length > 0) {
        content += `### Modern Connections\n`;
        c.modernConnections.forEach(connection => content += `- ${connection}\n`);
        content += '\n';
      }

      content += '---\n\n';
    });

    return content;
  }
}